import type { PointSystem, ResultCode, Sex, Title } from '../model/types.js';

/** Pairing systems the engine layer knows about. */
export type PairingSystemId =
  | 'dutch'
  | 'burstein'
  | 'dubov'
  | 'lim'
  | 'double-swiss'
  | 'uscf'
  | 'monrad'
  | 'keizer'
  | 'round-robin'
  | 'double-round-robin'
  | 'scheveningen'
  | 'knockout'
  | 'quads'
  | 'ladder'
  | 'arena'
  | 'manual';

/** A result pair for one board: white's code and black's code. */
export interface GameResult {
  readonly white: ResultCode;
  readonly black: ResultCode;
}

export const PENDING: GameResult = { white: '*', black: '*' };

export interface PlayerInfo {
  readonly name: string;
  readonly title?: Title | '';
  readonly sex?: Sex | null;
  readonly federation?: string;
  readonly fideId?: string;
  readonly nationalId?: string;
  readonly birthDate?: string;
  readonly club?: string;
  readonly team?: string;
  readonly email?: string;
  readonly phone?: string;
  /** Rating used for pairing (FIDE, or national when there is none). */
  readonly rating: number;
  readonly fideRating?: number;
  readonly nationalRating?: number;
}

export type ByeKind = 'H' | 'F' | 'Z';

export interface ByeRequest {
  readonly round: number;
  readonly kind: ByeKind;
}

export interface Entrant extends PlayerInfo {
  /** Stable id (never reused). */
  readonly id: string;
  /** Pairing number / TPN, 1-based, contiguous. */
  readonly pairingNumber: number;
  /** Rounds after which the player no longer takes part (withdrawn), or null. */
  readonly withdrawnAfterRound: number | null;
  readonly byeRequests: readonly ByeRequest[];
}

export interface Game {
  readonly board: number;
  readonly white: string;
  readonly black: string;
  readonly result: GameResult;
}

/** Byes as stored in a round: 'U' pairing-allocated, or a requested/absence bye. */
export interface RoundBye {
  readonly player: string;
  readonly kind: 'U' | ByeKind;
}

export interface Round {
  readonly number: number;
  readonly games: readonly Game[];
  readonly byes: readonly RoundBye[];
  readonly published: boolean;
}

export interface TournamentSettings {
  readonly name: string;
  readonly city: string;
  readonly federation: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly chiefArbiter: string;
  readonly deputyArbiters: readonly string[];
  readonly timeControl: string;
  readonly rounds: number;
  readonly system: PairingSystemId;
  readonly pointSystem: PointSystem;
  /** Colour of the top seed in round 1. */
  readonly initialColour: 'w' | 'b';
  /** Tiebreak specification strings, in order (e.g. "BH:C1", "SB", "DE"). */
  readonly tiebreaks: readonly string[];
  /** Acceleration method, if any ("baku" for Baku acceleration). */
  readonly acceleration: 'none' | 'baku';
}

export interface Tournament {
  readonly id: string;
  readonly settings: TournamentSettings;
  readonly players: readonly Entrant[];
  readonly rounds: readonly Round[];
}
